import {
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRTCClient,
  useRemoteUsers,
  useClientEvent,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from "agora-rtc-react";

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { config } from "./config/config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  CallEnd,
  FlipCameraIos,
  InsertDriveFileOutlined,
  MessageOutlined,
  Mic,
  MicOff,
  VideocamOffOutlined,
  VideocamOutlined,
  AssignmentOutlined,
  BlurOn,
  BlurOff,
} from "@mui/icons-material";
import useWindowDimensions from "../../hooks/windowSize";
import useDeviceDetector from "../../hooks/deviceDetector";
import { AppointmentChatProvider, useAppointmentChat } from "../../hooks/appointmentChat";
import { useAuth } from "../../hooks/auth";
import { UserInfoResponse } from "../../hooks/api/Login/interfaces/userInfo.response";
import { useAppointmentService } from "../../hooks/api/Appointment";
import { DraggableLocalVideo } from "./components/DraggableLocalVideo";
import { MeetStyleRemoteVideo } from "./components/MeetStyleRemoteVideo";
import { VideoCallFrame } from "./components/VideoCallFrame";
import { WaitingMessage } from "./components/WaitingMessage";
import { ChatPanel } from "./components/ChatPanel";
import { FilesPanel } from "./components/FilesPanel";
import { MedicalRecordModal } from "./components/MedicalRecordModal";
import { flowLog } from "../../utils/flowLog";
import { videoFrameContainerSx } from "./videoFrameStyles";
import { useVirtualBackgroundBlur } from "./hooks/useVirtualBackgroundBlur";

// Define the shape of the Agora context
interface AgoraContextType {
  localCameraTrack: ICameraVideoTrack | null;
  localMicrophoneTrack: IMicrophoneAudioTrack | null;
  children: React.ReactNode;
}

// Create the Agora context
const AgoraContext = createContext<AgoraContextType | null>(null);

// AgoraProvider component to provide the Agora context to its children
export const AgoraProvider: React.FC<AgoraContextType> = ({
  children,
  localCameraTrack,
  localMicrophoneTrack,
}) => (
  <AgoraContext.Provider
    value={{ localCameraTrack, localMicrophoneTrack, children }}
  >
    {children}
  </AgoraContext.Provider>
);

// Custom hook to access the Agora context
export const useAgoraContext = () => {
  const context = useContext(AgoraContext);
  if (!context)
    throw new Error("useAgoraContext must be used within an AgoraProvider");
  return context;
};

const CONSULTATION_ENDED_MESSAGE = "MAIS_SAUDE_CONSULTATION_ENDED";
const END_CONSULTATION_REQUEST_MESSAGE = "MAIS_SAUDE_END_CONSULTATION_REQUEST";
const CLOSE_VIDEO_CALL_WINDOW_MESSAGE = "MAIS_SAUDE_CLOSE_VIDEO_CALL_WINDOW";
const VIDEO_CALL_PARTICIPANTS_KEY = "@maisSaudeVideoCallParticipants";
const VIDEO_CALL_STARTED_AT_KEY = "@vivaseVideoCallStartedAt";
const MAIS_SAUDE_RETURN_KEY = "@maisSaudeVideoCallReturn";
const APPOINTMENT_STATUS_FINISHED = 3;
const FINISHED_APPOINTMENT_CLOSE_SECONDS = 15;
const DEFAULT_MAIS_SAUDE_ORIGIN = process.env.REACT_APP_MAIS_SAUDE_URL || "https://app.mqssaude.com.br";

type MaisSaudeReturnConfig = {
  returnUrl: string | null;
  origin: string | null;
  vivaseAppointmentId: number;
  maisSaudeAppointmentId: string | null;
};

const resolveMaisSaudeReturnConfig = (
  searchParams: URLSearchParams,
  vivaseAppointmentId?: string,
): MaisSaudeReturnConfig | null => {
  const returnUrlFromQuery = searchParams.get("maisSaudeReturnUrl");
  const originFromQuery = searchParams.get("maisSaudeOrigin");

  if (returnUrlFromQuery) {
    let maisSaudeAppointmentId: string | null = null;
    try {
      maisSaudeAppointmentId = new URL(returnUrlFromQuery).searchParams.get("appointmentId");
    } catch {
      maisSaudeAppointmentId = null;
    }

    return {
      returnUrl: returnUrlFromQuery,
      origin: originFromQuery || DEFAULT_MAIS_SAUDE_ORIGIN,
      vivaseAppointmentId: Number(vivaseAppointmentId),
      maisSaudeAppointmentId,
    };
  }

  const storedConfig = sessionStorage.getItem(MAIS_SAUDE_RETURN_KEY);
  if (!storedConfig) {
    return null;
  }

  try {
    const parsed = JSON.parse(storedConfig) as MaisSaudeReturnConfig;
    if (
      vivaseAppointmentId &&
      parsed.vivaseAppointmentId &&
      String(parsed.vivaseAppointmentId) !== String(vivaseAppointmentId)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

type VideoCallParticipants = {
  appointmentId: number;
  patientName: string;
  doctorName: string;
  patientUserId?: number;
  doctorUserId?: number;
};

const getStoredParticipants = (appointmentId?: string) => {
  const storedParticipants = sessionStorage.getItem(VIDEO_CALL_PARTICIPANTS_KEY);

  if (!storedParticipants) {
    return null;
  }

  try {
    const participants = JSON.parse(storedParticipants) as VideoCallParticipants;

    if (appointmentId && participants.appointmentId !== Number(appointmentId)) {
      return null;
    }

    return participants;
  } catch {
    return null;
  }
};

const FinishedAppointmentMessage = ({
  isDoctor,
  closeTargetOrigin,
}: {
  isDoctor: boolean;
  closeTargetOrigin: string | null;
}) => {
  const [seconds, setSeconds] = useState(FINISHED_APPOINTMENT_CLOSE_SECONDS);
  const [closeBlocked, setCloseBlocked] = useState(false);

  const requestWindowClose = useCallback(() => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        { type: CLOSE_VIDEO_CALL_WINDOW_MESSAGE },
        closeTargetOrigin || "*",
      );
    }

    window.close();

    window.setTimeout(() => {
      if (!window.closed) {
        setCloseBlocked(true);
      }
    }, 100);
  }, [closeTargetOrigin]);

  useEffect(() => {
    const countdown = window.setInterval(() => {
      setSeconds((currentSeconds) => Math.max(0, currentSeconds - 1));
    }, 1000);
    const closeTimeout = window.setTimeout(
      requestWindowClose,
      FINISHED_APPOINTMENT_CLOSE_SECONDS * 1000,
    );

    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(closeTimeout);
    };
  }, [requestWindowClose]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minHeight: "100vh",
        p: 3,
        textAlign: "center",
        backgroundColor: "#0B1D3A",
      }}
    >
      <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
        Consulta já finalizada
      </Typography>
      <Typography sx={{ color: "#E2E8F0", maxWidth: 480 }}>
        {isDoctor
          ? "Esta consulta já foi finalizada."
          : "O médico já finalizou a consulta."}
      </Typography>
      <Typography sx={{ color: "#94A3B8", maxWidth: 480 }}>
        {closeBlocked
          ? "O navegador bloqueou o fechamento automático. Feche esta aba manualmente."
          : `Esta aba será fechada automaticamente em ${seconds} segundos.`}
      </Typography>
      {closeBlocked && (
        <Button
          type="button"
          variant="contained"
          onClick={requestWindowClose}
          sx={{
            backgroundColor: "#0A6172",
            "&:hover": { backgroundColor: "#085461" },
          }}
        >
          Tentar fechar a aba
        </Button>
      )}
    </Box>
  );
};

const ChatToggleButton = ({
  showChat,
  isDesktop,
  onToggle,
}: {
  showChat: boolean;
  isDesktop: boolean;
  onToggle: () => void;
}) => {
  const { unreadCount } = useAppointmentChat();
  const buttonSize = isDesktop ? 48 : 40;

  return (
    <Badge
      badgeContent={unreadCount}
      color="error"
      invisible={unreadCount === 0 || showChat}
      max={99}
      overlap="circular"
    >
      <IconButton
        onClick={onToggle}
        sx={{
          bgcolor: showChat ? "#fff" : "#0A6172",
          width: buttonSize,
          height: buttonSize,
          "&:hover": {
            bgcolor: showChat ? "#f0f0f0" : "#085461",
          },
        }}
      >
        <MessageOutlined sx={{ color: showChat ? "#0A6172" : "#fff" }} />
      </IconButton>
    </Badge>
  );
};

const ActiveVideoCallContainer = () => {
  const { putFinishAppointment, putStartAppointment } = useAppointmentService();
  const { getUserData } = useAuth();
  const putStartAppointmentRef = useRef(putStartAppointment);
  const getUserDataRef = useRef(getUserData);
  putStartAppointmentRef.current = putStartAppointment;
  getUserDataRef.current = getUserData;
  const [user, setUser] = useState<UserInfoResponse>();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const device = useDeviceDetector();
  const { id } = useParams();
  const agoraEngine = useRTCClient();
  const { isLoading: isLoadingCam, localCameraTrack } = useLocalCameraTrack();
  const { isLoading: isLoadingMic, localMicrophoneTrack } =
    useLocalMicrophoneTrack();
  const {
    blurEnabled,
    toggleBlur,
    isSupported: isBlurSupported,
  } = useVirtualBackgroundBlur(localCameraTrack);
  const remoteUsers = useRemoteUsers();
  const [localVideoMuted, setLocalVideoMuted] = useState<boolean>(false);
  const [localAudioMuted, setLocalAudioMuted] = useState<boolean>(false);
  const [remoteVideoMuted, setRemoteVideoMuted] = useState<boolean>(false);
  const [cameraMode, setCameraMode] = useState<"environment" | "user">("user");
  const [showChat, setShowChat] = useState<boolean>(false);
  const [showFiles, setShowFiles] = useState<boolean>(false);
  const [showMedicalRecord, setShowMedicalRecord] = useState<boolean>(false);
  const [isFinishing, setIsFinishing] = useState<boolean>(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState<boolean>(false);
  const hadRemoteConnectionRef = useRef(false);
  const isDoctorRef = useRef(false);
  const isRedirectingRef = useRef(false);
  const redirectAfterLeaveRef = useRef<() => void>(() => {});
  const joinedAtRef = useRef<number | null>(null);
  const remoteConnectedAtRef = useRef<number | null>(null);
  const finishCallHandledRef = useRef(false);
  const finishCallRef = useRef<() => void>(() => {});
  const consultationStartedMarkedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const markConsultationStarted = useCallback((appointmentId: number) => {
    if (consultationStartedMarkedRef.current) {
      return;
    }

    consultationStartedMarkedRef.current = true;
    const startedAt = new Date().toISOString();
    sessionStorage.setItem(`${VIDEO_CALL_STARTED_AT_KEY}_${appointmentId}`, startedAt);

    putStartAppointmentRef.current(appointmentId).catch((error) => {
      consultationStartedMarkedRef.current = false;
      console.error("Error marking appointment start time:", error);
    });
  }, []);

  const isDoctor = user?.type === "Doctor";
  isDoctorRef.current = isDoctor;
  const isDesktop = device === "desktop";
  const hasRemoteUser = remoteUsers.length > 0;
  const searchParams = new URLSearchParams(location.search);
  const maisSaudeConfig = resolveMaisSaudeReturnConfig(searchParams, id);
  const maisSaudeReturnUrl = maisSaudeConfig?.returnUrl ?? null;
  const maisSaudeOrigin = maisSaudeConfig?.origin ?? null;
  const maisSaudeAppointmentId = maisSaudeConfig?.maisSaudeAppointmentId ?? null;
  const isIntegrationSession = !!localStorage.getItem("@integrationToken");
  const participants = getStoredParticipants(id);
  const localParticipantName = isDoctor
    ? participants?.doctorName || "Médico"
    : participants?.patientName || "Paciente";

  const flowRole = isDoctor ? "DOCTOR" : "PATIENT";
  const flowScope = `${flowRole}][VIDEO`;
  const consultationId = id ?? null;
  const appointmentId = participants?.appointmentId ?? null;

  useEffect(() => {
    joinedAtRef.current = Date.now();

    if (id) {
      markConsultationStarted(Number(id));
    }

    flowLog(flowScope, "Entrada na sala de videochamada", {
      screen: "VIDEO",
      action: "room_entered",
      role: flowRole,
      consultationId,
      appointmentId,
      roomId: consultationId,
      urlParams: {
        maisSaudeReturnUrl,
        maisSaudeOrigin,
      },
      userType: user?.type,
    });
  }, [flowScope, flowRole, consultationId, appointmentId, maisSaudeReturnUrl, maisSaudeOrigin, user?.type, id, markConsultationStarted]);

  const getMaisSaudeReturnUrlWithAppointment = (baseReturnUrl = maisSaudeReturnUrl) => {
    if (!baseReturnUrl) {
      return null;
    }

    try {
      const returnUrl = new URL(baseReturnUrl);

      if (!returnUrl.searchParams.get("consultationEnded")) {
        returnUrl.searchParams.set("consultationEnded", "1");
      }

      if (!returnUrl.searchParams.get("initialTab")) {
        returnUrl.searchParams.set("initialTab", "realizadas");
      }

      return returnUrl.toString();
    } catch {
      return baseReturnUrl;
    }
  };

  const buildMaisSaudeDoctorReturnUrl = () => {
    if (maisSaudeReturnUrl) {
      return getMaisSaudeReturnUrlWithAppointment(maisSaudeReturnUrl);
    }

    const origin = maisSaudeOrigin || DEFAULT_MAIS_SAUDE_ORIGIN;
    if (!origin || !maisSaudeAppointmentId) {
      return null;
    }

    try {
      const returnUrl = new URL("/doctor/dashboard/appointments", origin);
      returnUrl.searchParams.set("consultationEnded", "1");
      returnUrl.searchParams.set("appointmentId", maisSaudeAppointmentId);
      returnUrl.searchParams.set("initialTab", "realizadas");
      return returnUrl.toString();
    } catch {
      return null;
    }
  };

  const fetchUserData = useCallback(() => {
    const user = getUserDataRef.current();
    setUser(user);
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const toggleChat = () => {
    setShowFiles(false);
    setShowChat((previous) => !previous);
  };

  const toggleFiles = () => {
    setShowChat(false);
    setShowFiles(!showFiles);
  };

  // Publish local tracks
  usePublish([localMicrophoneTrack, localCameraTrack]);

  // Join the Agora channel with the specified configuration
  useJoin({
    appid: config.appId,
    channel: String(id),
    token: null,
    uid: config.uid,
  });

  useClientEvent(agoraEngine, "user-joined", (user) => {
    flowLog(flowScope, "Participante entrou no canal WebRTC", {
      screen: "VIDEO",
      action: "webrtc_user_joined",
      role: flowRole,
      consultationId,
      appointmentId,
      remoteUid: user.uid,
      timeToConnectionMs: joinedAtRef.current ? Date.now() - joinedAtRef.current : null,
    });
    hadRemoteConnectionRef.current = true;
    remoteConnectedAtRef.current = Date.now();
  });

  useClientEvent(agoraEngine, "user-left", (user) => {
    flowLog(flowScope, "Participante saiu do canal WebRTC", {
      screen: "VIDEO",
      action: "webrtc_user_left",
      role: flowRole,
      consultationId,
      appointmentId,
      remoteUid: user.uid,
    });

    if (!isDoctorRef.current && hadRemoteConnectionRef.current && !isRedirectingRef.current) {
      flowLog(flowScope, "Encerramento automático após saída do médico", {
        screen: "VIDEO",
        action: "call_ended_participant_left",
        role: flowRole,
        consultationId,
        appointmentId,
      });
      isRedirectingRef.current = true;
      finishCall();
    }
  });

  useClientEvent(agoraEngine, "user-published", (user, mediaType) => {
    if (mediaType === "video") {
      setRemoteVideoMuted(false);
    }
  });

  useClientEvent(agoraEngine, "user-unpublished", (user, mediaType) => {
    if (mediaType === "video") {
      setRemoteVideoMuted(true);
    }
  });

  useEffect(() => {
    return () => {
      localCameraTrack?.close();
    };
  }, [localCameraTrack]);

  useEffect(() => {
    return () => {
      localMicrophoneTrack?.close();
    };
  }, [localMicrophoneTrack]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isRedirectingRef.current || finishCallHandledRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    localMicrophoneTrack?.setMuted(localAudioMuted);
  }, [localAudioMuted, localMicrophoneTrack]);

  useEffect(() => {
    localCameraTrack?.setMuted(localVideoMuted);
  }, [localVideoMuted, localCameraTrack]);

  useEffect(() => {
    if (hasRemoteUser) {
      hadRemoteConnectionRef.current = true;

      if (!remoteConnectedAtRef.current) {
        remoteConnectedAtRef.current = Date.now();

        flowLog(flowScope, "Consulta iniciada — participante remoto conectado", {
          screen: "VIDEO",
          action: "consultation_started",
          role: flowRole,
          consultationId,
          appointmentId,
          timeToConnectionMs: joinedAtRef.current ? Date.now() - joinedAtRef.current : null,
        });
      }
    } else if (joinedAtRef.current) {
      flowLog(flowScope, "Aguardando participante remoto", {
        screen: "VIDEO",
        action: "waiting_for_remote",
        role: flowRole,
        consultationId,
        appointmentId,
        waitingMs: Date.now() - joinedAtRef.current,
      });
    }
  }, [hasRemoteUser, flowScope, flowRole, consultationId, appointmentId]);

  const handleAppointmentFinished = () => {
    flowLog(flowScope, "Evento socket appointment-finished recebido", {
      screen: "VIDEO",
      action: "socket_appointment_finished",
      role: flowRole,
      consultationId,
      appointmentId,
    });

    if (!isDoctorRef.current && !isRedirectingRef.current) {
      isRedirectingRef.current = true;
      redirectAfterLeaveRef.current();
    }
  };

  const notifyMaisSaudeConsultationEnded = (targetOrigin = maisSaudeOrigin) => {
    if (!window.opener || window.opener.closed) {
      flowLog(flowScope, "Sem window.opener — postMessage para Mais Saúde ignorado", {
        screen: "VIDEO",
        action: "notify_mais_saude_skipped",
        role: flowRole,
        consultationId,
        appointmentId,
      });
      return;
    }

    flowLog(flowScope, "Evento MAIS_SAUDE_CONSULTATION_ENDED emitido para Mais Saúde", {
      screen: "VIDEO",
      action: "notify_mais_saude_consultation_ended",
      role: flowRole,
      consultationId,
      appointmentId,
      maisSaudeOrigin: targetOrigin,
      callDurationMs: joinedAtRef.current ? Date.now() - joinedAtRef.current : null,
    });

    window.opener.postMessage(
      {
        type: CONSULTATION_ENDED_MESSAGE,
        appointmentId: maisSaudeAppointmentId ? Number(maisSaudeAppointmentId) : undefined,
      },
      targetOrigin || "*"
    );
  };

  const returnToMaisSaude = (doctorReturnUrl?: string | null) => {
    const resolvedReturnUrl =
      doctorReturnUrl ||
      buildMaisSaudeDoctorReturnUrl() ||
      getMaisSaudeReturnUrlWithAppointment() ||
      maisSaudeReturnUrl;
    const hasOpener = !!(window.opener && !window.opener.closed);

    if (hasOpener) {
      notifyMaisSaudeConsultationEnded();
      window.close();

      setTimeout(() => {
        if (!window.closed && resolvedReturnUrl) {
          window.location.replace(resolvedReturnUrl);
        }
      }, 300);

      return true;
    }

    if (resolvedReturnUrl) {
      flowLog(flowScope, "Retorno para Mais Saúde iniciado", {
        screen: "VIDEO",
        action: "return_to_mais_saude",
        role: flowRole,
        consultationId,
        appointmentId,
        returnUrl: resolvedReturnUrl,
        hasOpener: false,
      });

      window.location.replace(resolvedReturnUrl);
      return true;
    }

    return false;
  };

  const redirectAfterLeave = () => {
    if (finishCallHandledRef.current) {
      flowLog(flowScope, "redirectAfterLeave ignorado (idempotência)", {
        screen: "VIDEO",
        action: "redirect_duplicate_ignored",
        role: flowRole,
        consultationId,
        appointmentId,
      });
      return;
    }

    finishCallHandledRef.current = true;

    flowLog(flowScope, "Redirecionamento pós-consulta iniciado", {
      screen: "VIDEO",
      action: "redirect_after_leave",
      role: flowRole,
      consultationId,
      appointmentId,
      isDoctor,
      callDurationMs: joinedAtRef.current ? Date.now() - joinedAtRef.current : null,
    });

    localCameraTrack?.close();
    localMicrophoneTrack?.close();

    if (isDoctor && isIntegrationSession) {
      const doctorReturnUrl = buildMaisSaudeDoctorReturnUrl();

      flowLog("DOCTOR][VIDEO", "Encerramento integração Mais Saúde", {
        screen: "VIDEO",
        action: "integration_doctor_return",
        role: "DOCTOR",
        consultationId,
        appointmentId,
        maisSaudeAppointmentId,
        doctorReturnUrl,
        hasOpener: !!(window.opener && !window.opener.closed),
      });

      if (returnToMaisSaude(doctorReturnUrl)) {
        sessionStorage.removeItem(MAIS_SAUDE_RETURN_KEY);
        return;
      }
    }

    const shouldReturnToMaisSaude =
      !!maisSaudeReturnUrl ||
      !!maisSaudeAppointmentId ||
      !!(window.opener && !window.opener.closed);

    if (shouldReturnToMaisSaude && returnToMaisSaude()) {
      sessionStorage.removeItem(MAIS_SAUDE_RETURN_KEY);
      return;
    }

    if (isDoctor) {
      flowLog("DOCTOR][VIDEO", "Médico redirecionado para /consultas", {
        screen: "VIDEO",
        action: "navigate_consultas",
        role: "DOCTOR",
        consultationId,
        appointmentId,
      });
      navigate("/consultas");
      return;
    }

    const documentToken = localStorage.getItem('@documentToken');
    if (documentToken) {
      flowLog(flowScope, "Redirecionamento para /documents", {
        screen: "VIDEO",
        action: "navigate_documents",
        role: flowRole,
        consultationId,
        appointmentId,
        hasDocumentToken: true,
      });
      navigate(`/documents/${documentToken}${location.search}`);
      return;
    }

    navigate("/print/" + id);
  };
  redirectAfterLeaveRef.current = redirectAfterLeave;

  const finishDoctorAppointment = () => {
    if (isFinishing) {
      flowLog("DOCTOR][VIDEO", "Finalização ignorada — já em andamento", {
        screen: "VIDEO",
        action: "finish_duplicate_ignored",
        role: "DOCTOR",
        consultationId,
        appointmentId,
      });
      return;
    }

    setIsFinishing(true);

    flowLog("DOCTOR][VIDEO", "Médico clicou em encerrar videochamada", {
      screen: "VIDEO",
      action: "doctor_end_call_clicked",
      role: "DOCTOR",
      consultationId,
      appointmentId,
      callDurationMs: joinedAtRef.current ? Date.now() - joinedAtRef.current : null,
    });

    putFinishAppointment(Number(id))
      .then(() => {
        flowLog("DOCTOR][VIDEO", "Consulta finalizada no backend Vivase", {
          screen: "VIDEO",
          action: "finish_appointment_api_success",
          role: "DOCTOR",
          consultationId,
          appointmentId,
        });
        redirectAfterLeave();
      })
      .catch((error) => {
        finishCallHandledRef.current = false;
        flowLog("DOCTOR][VIDEO", "Erro ao finalizar consulta no backend", {
          screen: "VIDEO",
          action: "finish_appointment_api_error",
          role: "DOCTOR",
          consultationId,
          appointmentId,
          error: error instanceof Error ? error.message : String(error),
        });
        console.error("Error finishing appointment:", error);
        setIsFinishing(false);
      });
  };

  const finishCall = () => {
    if (isDoctor) {
      finishDoctorAppointment();
    } else {
      redirectAfterLeave();
    }
  };
  finishCallRef.current = finishCall;

  const handleLeaveClick = () => {
    flowLog(flowScope, "Botão encerrar chamada clicado", {
      screen: "VIDEO",
      action: "leave_call_clicked",
      role: flowRole,
      consultationId,
      appointmentId,
    });

    setLeaveConfirmOpen(true);
  };

  const handleLeaveCancel = () => {
    setLeaveConfirmOpen(false);
    flowLog(flowScope, "Saída da chamada cancelada pelo usuário", {
      screen: "VIDEO",
      action: "leave_call_cancelled",
      role: flowRole,
      consultationId,
      appointmentId,
    });
  };

  const handleLeaveConfirm = () => {
    setLeaveConfirmOpen(false);

    if (isDoctor) {
      finishDoctorAppointment();
      return;
    }

    finishCall();
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (maisSaudeOrigin && event.origin !== maisSaudeOrigin) {
        return;
      }

      if (event.data?.type !== END_CONSULTATION_REQUEST_MESSAGE) {
        return;
      }

      flowLog(flowScope, "Evento END_CONSULTATION_REQUEST recebido do Mais Saúde", {
        screen: "VIDEO",
        action: "end_consultation_request_received",
        role: flowRole,
        consultationId,
        appointmentId,
        eventOrigin: event.origin,
      });

      finishCallRef.current();
    };

    flowLog(flowScope, "Listener postMessage registrado", {
      screen: "VIDEO",
      action: "message_listener_registered",
      role: flowRole,
      consultationId,
      appointmentId,
      maisSaudeOrigin,
    });

    window.addEventListener("message", handleMessage);

    return () => {
      flowLog(flowScope, "Listener postMessage removido (cleanup)", {
        screen: "VIDEO",
        action: "message_listener_removed",
        role: flowRole,
        consultationId,
        appointmentId,
      });
      window.removeEventListener("message", handleMessage);
    };
  }, [maisSaudeOrigin, isDoctor, id, localCameraTrack, localMicrophoneTrack, location.search, flowScope, flowRole, consultationId, appointmentId]);

  const changeDevice = () => {
    if (cameraMode === "environment") {
      localCameraTrack?.setDevice({ facingMode: "user" });
      setCameraMode("user");
    } else {
      localCameraTrack?.setDevice({ facingMode: "environment" });
      setCameraMode("environment");
    }
  };

  // Check if devices are still loading
  const deviceLoading = isLoadingMic || isLoadingCam;
  if (deviceLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#000",
        }}
      >
        <CircularProgress size={48} sx={{ color: "#0A6172", mb: 2 }} />
        <Typography sx={{ color: "#fff" }}>Carregando dispositivos...</Typography>
      </Box>
    );
  }

  // Show loading overlay when finishing appointment
  if (isFinishing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#000",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#0A6172", mb: 3 }} />
        <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
          Finalizando consulta...
        </Typography>
        <Typography variant="body2" sx={{ color: "#94A3B8" }}>
          Aguarde enquanto os documentos são processados
        </Typography>
      </Box>
    );
  }

  // Calculate main video area width based on panel visibility
  const mainVideoWidth = isDesktop && (showChat || showFiles)
    ? windowWidth - Math.min(400, windowWidth * 0.3)
    : windowWidth;

  const doctorDisplayName = participants?.doctorName || "Médico";
  const patientDisplayName = participants?.patientName || "Paciente";
  const frameSidebarWidth = isDesktop ? 112 : 0;
  const framePaddingX = isDesktop ? 40 : 24;
  const framePaddingY = isDesktop ? 40 : 24;
  const frameNamesBarHeight = isDesktop ? 28 : 24;
  const frameGap = isDesktop ? 12 : 8;
  const videoAreaWidth = mainVideoWidth - frameSidebarWidth - framePaddingX;
  const videoAreaHeight =
    windowHeight - framePaddingY - frameNamesBarHeight - frameGap;

  const videoCallContent = (
    <AgoraProvider
      localCameraTrack={localCameraTrack}
      localMicrophoneTrack={localMicrophoneTrack}
    >
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100dvh",
          maxHeight: "-webkit-fill-available",
          backgroundColor: "#000",
          overflow: "hidden",
        }}
      >
        {/* Área principal com moldura, logo e nomes */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: mainVideoWidth,
            height: "100%",
          }}
        >
          <VideoCallFrame
            doctorName={doctorDisplayName}
            patientName={patientDisplayName}
            isDesktop={isDesktop}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                ...videoFrameContainerSx,
              }}
            >
              {hasRemoteUser
                ? remoteUsers.map((remoteUser) => (
                    <MeetStyleRemoteVideo
                      key={remoteUser.uid}
                      user={remoteUser}
                      playVideo={!remoteVideoMuted}
                    />
                  ))
                : null}
              {hasRemoteUser && remoteVideoMuted ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#1a1a1a",
                  }}
                >
                  <VideocamOffOutlined sx={{ fontSize: 80, color: "#444" }} />
                  <Typography sx={{ color: "#666", mt: 2 }}>
                    Câmera desativada
                  </Typography>
                </Box>
              ) : null}

              <WaitingMessage isDoctor={isDoctor} hasRemoteUser={hasRemoteUser} />

              <DraggableLocalVideo
                localCameraTrack={localCameraTrack}
                localVideoMuted={localVideoMuted}
                participantName={localParticipantName}
                windowWidth={videoAreaWidth}
                windowHeight={videoAreaHeight}
              />
            </Box>
          </VideoCallFrame>
        </Box>

        {/* Mobile: Camera flip button */}
        {!isDesktop && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: frameSidebarWidth + 16,
              zIndex: 100,
            }}
          >
            <IconButton
              onClick={changeDevice}
              sx={{
                bgcolor: "rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <FlipCameraIos sx={{ color: "#fff" }} />
            </IconButton>
          </Box>
        )}

        {/* Control Bar */}
        <Box
          sx={{
            position: "absolute",
            bottom: isDesktop ? 32 : 16,
            left: "50%",
            transform: `translateX(-50%) ${isDesktop && (showChat || showFiles) ? `translateX(-${Math.min(200, windowWidth * 0.15)}px)` : ""}`,
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            gap: isDesktop ? 2 : 1,
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.6)",
            borderRadius: 4,
            px: isDesktop ? 3 : 1.5,
            py: isDesktop ? 1.5 : 1,
            maxWidth: isDesktop ? "none" : "calc(100vw - 16px)",
            zIndex: 150,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isDesktop ? 2 : 1,
              width: "100%",
              flexWrap: isDesktop ? "nowrap" : "nowrap",
            }}
          >
            {/* Camera Toggle */}
            <IconButton
              onClick={() => setLocalVideoMuted(!localVideoMuted)}
              sx={{
                bgcolor: localVideoMuted ? "#fff" : "#0A6172",
                width: isDesktop ? 48 : 40,
                height: isDesktop ? 48 : 40,
                p: isDesktop ? 1 : 0.75,
                "&:hover": {
                  bgcolor: localVideoMuted ? "#f0f0f0" : "#085461",
                },
              }}
            >
              {localVideoMuted ? (
                <VideocamOffOutlined sx={{ color: "#0A6172", fontSize: isDesktop ? 24 : 20 }} />
              ) : (
                <VideocamOutlined sx={{ color: "#fff", fontSize: isDesktop ? 24 : 20 }} />
              )}
            </IconButton>

            {/* Background Blur Toggle */}
            {isBlurSupported && (
              <IconButton
                onClick={toggleBlur}
                title={blurEnabled ? "Desativar desfoque do fundo" : "Ativar desfoque do fundo"}
                sx={{
                  bgcolor: blurEnabled ? "#0A6172" : "#fff",
                  width: isDesktop ? 48 : 40,
                  height: isDesktop ? 48 : 40,
                  p: isDesktop ? 1 : 0.75,
                  "&:hover": {
                    bgcolor: blurEnabled ? "#085461" : "#f0f0f0",
                  },
                }}
              >
                {blurEnabled ? (
                  <BlurOn sx={{ color: "#fff", fontSize: isDesktop ? 24 : 20 }} />
                ) : (
                  <BlurOff sx={{ color: "#0A6172", fontSize: isDesktop ? 24 : 20 }} />
                )}
              </IconButton>
            )}

            {/* Microphone Toggle */}
            <IconButton
              onClick={() => setLocalAudioMuted(!localAudioMuted)}
              sx={{
                bgcolor: localAudioMuted ? "#fff" : "#0A6172",
                width: isDesktop ? 48 : 40,
                height: isDesktop ? 48 : 40,
                p: isDesktop ? 1 : 0.75,
                "&:hover": {
                  bgcolor: localAudioMuted ? "#f0f0f0" : "#085461",
                },
              }}
            >
              {localAudioMuted ? (
                <MicOff sx={{ color: "#0A6172", fontSize: isDesktop ? 24 : 20 }} />
              ) : (
                <Mic sx={{ color: "#fff", fontSize: isDesktop ? 24 : 20 }} />
              )}
            </IconButton>

            {/* Chat */}
            {id ? <ChatToggleButton showChat={showChat} isDesktop={isDesktop} onToggle={toggleChat} /> : null}

            {/* Files */}
            <IconButton
              onClick={toggleFiles}
              sx={{
                bgcolor: showFiles ? "#fff" : "#0A6172",
                width: isDesktop ? 48 : 40,
                height: isDesktop ? 48 : 40,
                p: isDesktop ? 1 : 0.75,
                "&:hover": {
                  bgcolor: showFiles ? "#f0f0f0" : "#085461",
                },
              }}
            >
              <InsertDriveFileOutlined
                sx={{ color: showFiles ? "#0A6172" : "#fff", fontSize: isDesktop ? 24 : 20 }}
              />
            </IconButton>

            {/* Medical Record - Doctor Only */}
            {isDoctor && (
              <IconButton
                onClick={() => setShowMedicalRecord(true)}
                sx={{
                  bgcolor: "#0A6172",
                  width: isDesktop ? 48 : 40,
                  height: isDesktop ? 48 : 40,
                  p: isDesktop ? 1 : 0.75,
                  "&:hover": { bgcolor: "#085461" },
                }}
              >
                <AssignmentOutlined sx={{ color: "#fff", fontSize: isDesktop ? 24 : 20 }} />
              </IconButton>
            )}
          </Box>

          {/* End Call */}
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <IconButton
              onClick={handleLeaveClick}
              sx={{
                bgcolor: "#DC2626",
                width: isDesktop ? 56 : 44,
                height: isDesktop ? 56 : 44,
                p: isDesktop ? 1.25 : 0.75,
                "&:hover": { bgcolor: "#B91C1C" },
              }}
            >
              <CallEnd sx={{ color: "#fff", fontSize: isDesktop ? 28 : 22 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Chat Panel */}
        {id && (
          <ChatPanel
            appointmentId={Number(id)}
            onClose={() => setShowChat(false)}
            isDesktop={isDesktop}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
            isOpen={showChat}
            participants={participants}
          />
        )}

        {/* Files Panel */}
        {showFiles && id && (
          <FilesPanel
            appointmentId={Number(id)}
            onClose={() => setShowFiles(false)}
            isDesktop={isDesktop}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
          />
        )}

        {/* Medical Record Modal */}
        <MedicalRecordModal
          open={showMedicalRecord}
          onClose={() => setShowMedicalRecord(false)}
        />

        <Dialog
          open={leaveConfirmOpen}
          onClose={handleLeaveCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, color: "#0F172A", fontWeight: 700 }}>
            {isDoctor ? "Deseja sair da chamada?" : "Deseja realmente sair da chamada?"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "#475569" }}>
              {isDoctor
                ? "Você pode cancelar esta ação ou finalizar a consulta antes de sair."
                : "Essa ação encerra sua participação na videochamada."}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Button onClick={handleLeaveCancel} variant="outlined" color="inherit">
              Cancelar
            </Button>
            <Button onClick={handleLeaveConfirm} variant="contained" color="error">
              {isDoctor ? "Finalizar consulta" : "Sim, sair"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AgoraProvider>
  );

  if (!id) {
    return videoCallContent;
  }

  return (
    <AppointmentChatProvider
      appointmentId={Number(id)}
      flowScope={flowScope}
      flowRole={flowRole}
      consultationId={consultationId}
      isChatOpen={showChat}
      onAppointmentFinished={handleAppointmentFinished}
    >
      {videoCallContent}
    </AppointmentChatProvider>
  );
};

export const VideoCallContainer = () => {
  const { id } = useParams();
  const location = useLocation();
  const { getAppointment } = useAppointmentService();
  const { getUserData } = useAuth();
  const [validationStatus, setValidationStatus] = useState<
    "loading" | "ready" | "finished" | "error"
  >("loading");
  const [validationError, setValidationError] = useState(
    "Não foi possível verificar o status da consulta."
  );
  const getAppointmentRef = useRef(getAppointment);
  getAppointmentRef.current = getAppointment;
  const isDoctor = getUserData()?.type === "Doctor";
  const maisSaudeConfig = resolveMaisSaudeReturnConfig(
    new URLSearchParams(location.search),
    id,
  );
  const closeTargetOrigin = maisSaudeConfig?.origin ?? null;

  useEffect(() => {
    let cancelled = false;

    if (!id) {
      setValidationError("Consulta não encontrada.");
      setValidationStatus("error");
      return () => {
        cancelled = true;
      };
    }

    const appointmentId = Number(id);
    if (!Number.isInteger(appointmentId) || appointmentId <= 0) {
      setValidationError("Consulta inválida.");
      setValidationStatus("error");
      return () => {
        cancelled = true;
      };
    }

    setValidationStatus("loading");
    getAppointmentRef.current(appointmentId)
      .then((appointment) => {
        if (cancelled) {
          return;
        }

        const isFinished = Number(appointment?.appointment_status) === APPOINTMENT_STATUS_FINISHED;
        setValidationStatus(isFinished ? "finished" : "ready");
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setValidationError("Não foi possível verificar o status da consulta.");
        setValidationStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (validationStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          height: "100vh",
          backgroundColor: "#0B1D3A",
        }}
      >
        <CircularProgress size={48} sx={{ color: "#0A6172" }} />
        <Typography sx={{ color: "#fff" }}>Verificando status da consulta...</Typography>
      </Box>
    );
  }

  if (validationStatus === "finished") {
    return (
      <FinishedAppointmentMessage
        isDoctor={isDoctor}
        closeTargetOrigin={closeTargetOrigin}
      />
    );
  }

  if (validationStatus === "error") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          p: 3,
          textAlign: "center",
          backgroundColor: "#0B1D3A",
        }}
      >
        <Typography sx={{ color: "#fff" }}>{validationError}</Typography>
      </Box>
    );
  }

  return <ActiveVideoCallContainer />;
};

export default VideoCallContainer;
